import editorialStyle from "../../../components/editorial/style.scss";

import Link from "../../../components/link";
import FixedTable from "../../../components/fixed-table";
import NoWrap from "../../../components/no-wrap";

import compatibilityData from "../data/alternator-chargers/compatibility.json";
import powerStationData from "../../../data/power-stations/ecoflow";
import { Fragment } from "preact";

// -----------------------------
// Helpers
// -----------------------------
const formatNumber = (value, suffix, fallback = "--", digits = 0) => {
  return value
    ? `${new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 0,
        maximumFractionDigits: digits,
      }).format(value)}${suffix}`
    : fallback;
};

const getRating = (product, powerLevel, connector) => {
  return formatNumber(product.chargers?.[powerLevel]?.[connector], "W");
};

// Convert ports object → readable string
const formatPortsForType = (ports, portType) => {
  const p = ports?.[portType];
  if (!p?.count) return "--";

  if (p.expandableTo) {
    return (
      <Fragment>
        {p.count}–{p.expandableTo} <sup>1</sup>
      </Fragment>
    );
  }

  return p.count;
};

const mapPortToChargerKey = (port) => {
  if (port === "4+8") return "expansionPort";
  if (port === "XT150") return "expansionPort";
  return port; // XT60 stays XT60
};

// -----------------------------
// Flatten compatibility.json WITH group names
// -----------------------------
const flattenWithGroups = () => {
  return Object.entries(compatibilityData).flatMap(([groupName, products]) =>
    products.map((p) => ({ ...p, group: groupName }))
  );
};

// -----------------------------
// Filtering logic
// -----------------------------
const get4Plus8Products = () => {
  return flattenWithGroups().filter((p) => p.ports?.["4+8"]?.count > 0);
};

const getXT150Products = () => {
  return flattenWithGroups().filter((p) => p.ports?.XT150?.count > 0);
};

const getXT60Products = () => {
  return flattenWithGroups().filter((p) => p.ports?.XT60?.count > 0);
};

const getHP300Products = () => {
  return flattenWithGroups().filter((p) => p.ports?.HP300?.count > 0);
};

// -----------------------------
// Table Renderer (with Ports column)
// -----------------------------
const renderSimpleTable = (products, portType) => {
  const connector = mapPortToChargerKey(portType);

  // Track footnote as we go
  let shouldRenderFootnote = false;

  return (
    <Fragment>
      <FixedTable frameless>
        <table>
          <thead>
            <tr>
              <th class={editorialStyle.sticky}>Model</th>
              <th scope="col">{portType} Ports</th>
              <th scope="col">500W Charger</th>
              <th scope="col">800W Charger</th>
            </tr>
          </thead>

          <tbody>
            {products.map((product, index) => {
              const prev = products[index - 1];
              const isNewGroup = index === 0 || product.group !== prev.group;

              const data = powerStationData?.powerStations[product.name];

              // Track expandable ports
              if (product.ports?.[portType]?.expandableTo) {
                shouldRenderFootnote = true;
              }

              return (
                <tr
                  key={product.name}
                  class={isNewGroup ? editorialStyle.rowBorder : null}
                >
                  <th scope="row" class={editorialStyle.sticky}>
                    {data?.link?.href ? (
                      <Link
                        external
                        affiliateData={data.link.affiliateData}
                        affiliate={data.link.affiliate}
                        href={data.link.href}
                      >
                        {product.name}
                      </Link>
                    ) : (
                      product.name
                    )}
                  </th>

                  <td>
                    <NoWrap>
                      {formatPortsForType(product.ports, portType)}
                    </NoWrap>
                  </td>

                  <td>
                    <NoWrap>{getRating(product, "500", connector)}</NoWrap>
                  </td>

                  <td class={editorialStyle.cellDarken}>
                    <NoWrap>{getRating(product, "800", connector)}</NoWrap>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </FixedTable>

      {shouldRenderFootnote && (
        <p class="caption" style="margin-bottom: 2px;">
          <strong>1.</strong> Values above the minimum require an EcoFlow
          expansion accessory.
        </p>
      )}
    </Fragment>
  );
};

// -----------------------------
// Public Components
// -----------------------------
export const FourPlusEightTable = () => {
  const products = get4Plus8Products();
  return renderSimpleTable(products, "4+8"); // 4+8 uses expansionPort
};

export const XT150Table = () => {
  const products = getXT150Products();
  return renderSimpleTable(products, "XT150"); // XT150 uses expansionPort
};

export const XT60Table = () => {
  const products = getXT60Products();
  return renderSimpleTable(products, "XT60");
};

export const HP300Table = () => {
  const products = getHP300Products();
  return renderSimpleTable(products, "HP300");
};

export default {
  FourPlusEightTable,
  XT150Table,
  XT60Table,
  HP300Table,
};
